/**
 * Calques Martin trails_osm — spec partagée (miroir tools/trail_layer_spec.mjs).
 */
import type { FeatureCollection } from 'geojson'
import type { FilterSpecification, LineLayerSpecification } from 'maplibre-gl'

export const MARTIN_TRAILS_TILES_URL =
  import.meta.env.VITE_MARTIN_TRAILS_URL ||
  'https://api.trouvetonrefuge.com/tiles/trails_osm/{z}/{x}/{y}'

export const MARTIN_TRAILS_SOURCE_ID = 'martin-trails'

/** Tuiles Martin de la table trails_aqp (AQréseau+ : FCMQ motoneige, FQCQ quad). */
export const MARTIN_TRAILS_AQP_TILES_URL =
  import.meta.env.VITE_MARTIN_TRAILS_AQP_URL ||
  'https://api.trouvetonrefuge.com/tiles/trails_aqp/{z}/{x}/{y}'

export const MARTIN_TRAILS_AQP_SOURCE_ID = 'martin-trails-aqp'

export interface MartinTrailLayerSpec {
  key: string
  group: string
  color: string
  width: number
  minzoom: number
  excludeIfTypes?: string[]
  legacy?: boolean
  /** Trait en tirets (ex. hiking_minor). */
  dashed?: boolean
}

export const MARTIN_TRAIL_LAYER_SPECS: MartinTrailLayerSpec[] = [
  {
    key: 'hiking_minor',
    group: 'hiking',
    // Même orange que hiking legacy — trait fin en tirets
    color: '#f97316',
    width: 1,
    dashed: true,
    minzoom: 12,
    excludeIfTypes: ['hiking_major', 'hiking_standard'],
  },
  {
    key: 'hiking_standard',
    group: 'hiking',
    // Orange brand — contraste sur fond topo verdoyant (éviter le vert)
    color: '#ea580c',
    width: 1.5,
    minzoom: 9,
    excludeIfTypes: ['hiking_major'],
  },
  {
    key: 'hiking_major',
    group: 'hiking',
    color: '#c2410c',
    width: 2.2,
    minzoom: 8,
    excludeIfTypes: [],
  },
  {
    key: 'hiking',
    group: 'hiking',
    color: '#f97316',
    width: 1.4,
    minzoom: 9,
    excludeIfTypes: ['hiking_major', 'hiking_standard', 'hiking_minor'],
    legacy: true,
  },
  { key: 'cycling', group: 'cycling', color: '#1a6fb5', width: 1.4, minzoom: 9 },
  { key: 'mtb', group: 'mtb', color: '#2563eb', width: 1.7, minzoom: 9 },
  { key: 'nordic', group: 'nordic', color: '#00b4d8', width: 1.4, minzoom: 9 },
  { key: 'snowshoe', group: 'snowshoe', color: '#38bdf8', width: 1.4, minzoom: 9 },
  { key: 'alpine', group: 'alpine', color: '#e11d48', width: 1.7, minzoom: 9 },
  { key: 'atv', group: 'atv', color: '#a16207', width: 1.4, minzoom: 9 },
  { key: 'snowmobile', group: 'snowmobile', color: '#6366f1', width: 1.4, minzoom: 9 },
]

function trailTypesMvtContains(typeKey: string): unknown[] {
  const needle = ['concat', ',', typeKey, ',']
  const haystack = ['concat', ',', ['coalesce', ['get', 'trail_types_mvt'], ''], ',']
  return ['>=', ['index-of', needle, haystack], 0]
}

export function trailTypesFilter(typeKey: string, excludeIfTypes: string[] = []): FilterSpecification {
  const hasType: unknown[] = [
    'any',
    trailTypesMvtContains(typeKey),
    ['in', typeKey, ['coalesce', ['get', 'trail_types'], ['literal', []]]],
    [
      '>=',
      ['index-of', typeKey, ['to-string', ['coalesce', ['get', 'trail_types'], '']]],
      0,
    ],
  ]
  if (!excludeIfTypes.length) return hasType as FilterSpecification
  const exclusions: unknown[] = excludeIfTypes.map((ex) => [
    '!',
    [
      'any',
      trailTypesMvtContains(ex),
      ['in', ex, ['coalesce', ['get', 'trail_types'], ['literal', []]]],
    ],
  ])
  const filter: unknown[] = ['all', hasType, ...exclusions]
  return filter as FilterSpecification
}

export function buildMartinTrailLayerDefinitions(): LineLayerSpecification[] {
  return MARTIN_TRAIL_LAYER_SPECS.map((spec): LineLayerSpecification => ({
    id: `trails-${spec.key}`,
    type: 'line' as const,
    source: MARTIN_TRAILS_SOURCE_ID,
    'source-layer': 'trails_osm',
    filter: trailTypesFilter(spec.key, spec.excludeIfTypes ?? []),
    minzoom: spec.minzoom,
    layout: {
      'line-join': 'round',
      'line-cap': spec.dashed ? 'butt' : 'round',
      visibility: 'none',
    },
    paint: {
      'line-color': spec.color,
      'line-width': spec.dashed
        ? spec.width
        : [
            'interpolate',
            ['linear'],
            ['zoom'],
            8,
            spec.width * 0.5,
            10,
            spec.width * 0.75,
            12,
            spec.width,
            14,
            spec.width * 1.35,
          ],
      'line-opacity': 0.92,
      ...(spec.dashed ? { 'line-dasharray': [8, 4] } : {}),
    },
  }))
}

/**
 * Calques AQréseau+ — même rendu que leur équivalent OSM (atv / snowmobile),
 * distingués par le champ `source` ('fqcq' quad, 'fcmq' motoneige).
 * IDs alignés sur AQP_LAYER_MAP (mapTrailTypes.ts).
 */
export const AQP_TRAIL_LAYER_SPECS: {
  key: string
  aqpSource: string
  color: string
  width: number
  minzoom: number
}[] = [
  { key: 'atv-aqp', aqpSource: 'fqcq', color: '#a16207', width: 1.4, minzoom: 8 },
  { key: 'snowmobile-aqp', aqpSource: 'fcmq', color: '#6366f1', width: 1.4, minzoom: 8 },
]

export function buildAqpTrailLayerDefinitions(): LineLayerSpecification[] {
  return AQP_TRAIL_LAYER_SPECS.map((spec): LineLayerSpecification => ({
    id: `trails-${spec.key}`,
    type: 'line' as const,
    source: MARTIN_TRAILS_AQP_SOURCE_ID,
    'source-layer': 'trails_aqp',
    filter: ['==', ['get', 'source'], spec.aqpSource],
    minzoom: spec.minzoom,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      // Réseau provincial complet — affiché uniquement via le toggle Sentiers.
      visibility: 'none',
    },
    paint: {
      'line-color': spec.color,
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        spec.width * 0.5,
        10,
        spec.width * 0.75,
        12,
        spec.width,
        14,
        spec.width * 1.35,
      ],
      'line-opacity': 0.92,
    },
  }))
}

/** Calques contrôlés par le toggle « Randonnée ». */
export const HIKING_TRAIL_LAYER_IDS = MARTIN_TRAIL_LAYER_SPECS.filter(
  (s) => s.group === 'hiking',
).map((s) => `trails-${s.key}`)

export function layerIdsForTrailToggle(toggleId: string): string[] {
  if (toggleId === 'hiking') return HIKING_TRAIL_LAYER_IDS
  return [`trails-${toggleId}`]
}

/**
 * Calques « hit » invisibles (survol/clic) — même source/filtre que le
 * calque visible correspondant, mais une ligne large (quasi transparente)
 * pour une zone cliquable/survolable bien plus grande que le trait rendu
 * (1,4 à 3,2 px selon le type). Pattern repris de RouteCreator.tsx
 * (route-line-hit). Ne modifie jamais le style du calque visible.
 */
const TRAIL_HIT_LINE_WIDTH = 22

export function hitLayerId(layerId: string): string {
  return `${layerId}-hit`
}

function toHitLayerDefinition(def: LineLayerSpecification): LineLayerSpecification {
  return {
    ...def,
    id: hitLayerId(def.id),
    layout: { ...def.layout, visibility: 'none' },
    paint: {
      'line-color': '#000000',
      'line-width': TRAIL_HIT_LINE_WIDTH,
      // 0 et non 0.01 : le hit-testing MapLibre (queryRenderedFeatures,
      // mouseenter/click) se base sur la géométrie + line-width, pas sur
      // l'opacité rendue — donc totalement invisible n'enlève rien à
      // l'interactivité. À 0.01, des dizaines de segments qui se
      // chevauchent (embranchements denses, ex. Sainte-Julie) s'accumulent
      // par compositing alpha et forment une tache sombre visible au zoom
      // arrière — 0 élimine le problème à la source.
      'line-opacity': 0,
    },
  }
}

export function buildMartinTrailHitLayerDefinitions() {
  return buildMartinTrailLayerDefinitions().map(toHitLayerDefinition)
}

export function buildAqpTrailHitLayerDefinitions() {
  return buildAqpTrailLayerDefinitions().map(toHitLayerDefinition)
}

/** Calque de surbrillance (survol) — une seule géométrie à la fois, mise à jour dynamiquement. */
export const TRAIL_HIGHLIGHT_SOURCE_ID = 'trail-highlight'
export const TRAIL_HIGHLIGHT_LAYER_ID = 'trail-highlight-layer'

export function emptyFeatureCollection(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] }
}
